/**
 * useConversations Hook
 *
 * Real-time listener for user's conversation inbox.
 * Uses participantIds array-contains for efficient Firestore queries.
 *
 * @module features/messaging/hooks/useConversations
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  onSnapshot
} from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuth } from '@/hooks';
import ConversationService from '@/services/messaging/ConversationService';
import { isArchivedForUser, normalizeConversation } from '@/models/Conversation';

/**
 * @param {Object} options
 * @param {number} options.limit - Max conversations to fetch
 * @param {boolean} options.includeArchived - Include archived conversations
 * @param {boolean} options.autoSubscribe - Auto-start real-time listener
 * @param {string|null} options.schoolId - Filter to a specific school
 * @returns {Object}
 */
export default function useConversations({
  limit = 50,
  includeArchived = false,
  autoSubscribe = true,
  schoolId = null
} = {}) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real-time listener using participantIds array-contains
  useEffect(() => {
    if (!user?.uid || !autoSubscribe) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Efficient query: only fetch conversations where current user is a participant
      const q = query(
        collection(db, 'conversations'),
        where('participantIds', 'array-contains', user.uid),
        orderBy('lastMessageAt', 'desc'),
        firestoreLimit(limit)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const fetched = [];

          snapshot.forEach((doc) => {
            const data = doc.data();

            // Client-side: filter to active school if provided
            if (schoolId && data.schoolId && data.schoolId !== schoolId) {
              return;
            }

            // Client-side: skip archived unless requested
            if (!includeArchived && isArchivedForUser(data, user.uid)) {
              return;
            }

            fetched.push(normalizeConversation({ id: doc.id, ...data }));
          });

          setConversations(fetched);
          setLoading(false);
        },
        (err) => {
          console.error('Error in conversations listener:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up conversations listener:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [user?.uid, limit, includeArchived, autoSubscribe, schoolId]);

  // Computed values
  const unreadCount = useMemo(() =>
    conversations.reduce((total, conv) => total + (conv.unreadCounts?.[user?.uid] || 0), 0),
    [conversations, user?.uid]
  );

  const activeConversations = useMemo(() =>
    conversations.filter(conv => !isArchivedForUser(conv, user?.uid)),
    [conversations, user?.uid]
  );

  const archivedConversations = useMemo(() =>
    conversations.filter(conv => isArchivedForUser(conv, user?.uid)),
    [conversations, user?.uid]
  );

  // Actions
  const markAsRead = useCallback(async (conversationId) => {
    if (!user?.uid) return;
    await ConversationService.markAsRead(conversationId, user.uid);
  }, [user?.uid]);

  const archiveConversation = useCallback(async (conversationId) => {
    if (!user?.uid) return;
    await ConversationService.archiveConversation(conversationId, user.uid);
  }, [user?.uid]);

  const unarchiveConversation = useCallback(async (conversationId) => {
    if (!user?.uid) return;
    await ConversationService.unarchiveConversation(conversationId, user.uid);
  }, [user?.uid]);

  const muteConversation = useCallback(async (conversationId) => {
    if (!user?.uid) return;
    await ConversationService.muteConversation(conversationId, user.uid);
  }, [user?.uid]);

  const unmuteConversation = useCallback(async (conversationId) => {
    if (!user?.uid) return;
    await ConversationService.unmuteConversation(conversationId, user.uid);
  }, [user?.uid]);

  const createConversation = useCallback(async (conversationData) => {
    if (!user?.uid) throw new Error('User not authenticated');
    return ConversationService.createConversation({
      ...conversationData,
      createdBy: user.uid
    });
  }, [user?.uid]);

  return {
    conversations,
    activeConversations,
    archivedConversations,
    unreadCount,
    loading,
    error,
    markAsRead,
    archiveConversation,
    unarchiveConversation,
    muteConversation,
    unmuteConversation,
    createConversation
  };
}
